import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface VideoPlayerProps {
  src: string;
  poster?: string;
  title?: string;
  caption?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  controls?: boolean;
  width?: string;
  height?: string;
  className?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  poster,
  title,
  caption,
  autoPlay = false,
  loop = false,
  muted = false,
  controls = true,
  width = '100%',
  height = 'auto',
  className = '',
}) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [error, setError] = useState<string | null>(null);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(e => {
          console.error('Error playing video:', e);
          setError(t('videoPlayer.playbackError', 'Unable to play video'));
        });
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoClick = () => {
    if (!controls) {
      togglePlay();
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleError = () => {
    setError(t('videoPlayer.loadError', 'Video failed to load'));
  };

  return (
    <div className={`video-player-container relative ${className}`}>
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
          <div className="text-white p-4 bg-red-500 bg-opacity-90 rounded-md">
            {error}
          </div>
        </div>
      )}
      <video
        ref={videoRef}
        className="w-full h-auto rounded-lg shadow-lg"
        width={width}
        height={height}
        poster={poster}
        controls={controls}
        autoPlay={autoPlay}
        loop={loop}
        muted={muted}
        playsInline
        onClick={handleVideoClick}
        onPlay={handlePlay}
        onPause={handlePause}
        onError={handleError}
        aria-label={title || caption || 'Video content'}
      >
        <source src={src} type="video/mp4" />
        {/* Add caption track for accessibility */}
        <track
          kind="captions"
          srcLang="en"
          label="English captions"
          src=""
          default
        />
        {t('videoPlayer.browserSupport', 'Your browser does not support the video tag.')}
      </video>
      {caption && (
        <div className="mt-2 text-center text-sm text-gray-600">
          {caption}
        </div>
      )}
    </div>
  );
};

export default VideoPlayer; 